import { saveLuaScript, luaDebug, getLuaTemplate } from '@/api';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Modal, Switch, message } from 'antd';
import { formatChunk } from '@appguru/luafmt';
import 'monaco-editor/esm/vs/basic-languages/lua/lua.contribution';
import React, { useEffect, useRef, useState } from 'react';
import MonacoEditor from 'react-monaco-editor';
import codeIcon from '@/assets/images/code.svg';


interface LuaScriptEditorProps {
  product_id: string;
  initialValue?: string;
  initialEnabled?: boolean;
  initialRawData?: string;
  onSubmit?: (scriptContent: string, enabled: boolean) => void;
}

const LuaScriptEditor: React.FC<LuaScriptEditorProps> = ({
  product_id,
  initialValue = '',
  initialEnabled = false,
  initialRawData = '',
  onSubmit,
}) => {
  const [inputData, setInputData] = useState<string>('');
  const [rawData, setRawData] = useState<string>(initialRawData);
  const [parsedData, setParsedData] = useState<string>('');
  const [logsData, setLogsData] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(initialEnabled);
  const [saving, setSaving] = useState<boolean>(false);

  const inputEditorRef = useRef<any>(null);
  const luaEditorRef = useRef<any>(null);
  const outputEditorRef = useRef<any>(null);


  // 初始化时获取模板数据（仅当没有初始值时）
  useEffect(() => {
    if (initialValue !== undefined && initialValue !== null && initialValue !== '') {
      // 如果有初始值，直接使用
      setInputData(initialValue);
    } else {
      // 否则获取模板数据
      const fetchTemplate = async () => {
        try {
          const res = await getLuaTemplate<string>();
          if (res.success && res.result) {
            setInputData(res.result);
          }
        } catch (error) {
          console.error('Failed to fetch Lua template:', error);
        }
      };
      fetchTemplate();
    }
  }, [initialValue]);

  // 当 initialEnabled 变化时更新 enabled 状态
  useEffect(() => {
    if (initialEnabled !== undefined) {
      setEnabled(initialEnabled);
    }
  }, [initialEnabled]);

  // 处理保存按钮点击
  const handleConfirm = async () => {
    setSaving(true);
    try {
      const res = await saveLuaScript({
        product_id,
        lua_script: inputData,
        lua_script_enable: enabled,
      });

      if (res.success) {
        message.success('保存成功');
        if (onSubmit) {
          onSubmit(inputData, enabled);
        }
      } else {
        message.error(res.errorMsg || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setRawData('');
  };

  // 处理格式化按钮点击
  const handleFormat = () => {
    try {
      if (inputEditorRef.current) {
        const formattedCode = formatChunk(inputData);
        // 更新编辑器内容
        setInputData(formattedCode);
        inputEditorRef.current.setValue(formattedCode);
        message.success('格式化完成');
      }
    } catch (error) {
      console.error('Format error:', error);
      message.error('格式化失败');
    }
  };

  const handleTest = async () => {
    if (!inputData?.trim()) {
      message.warning('请先输入测试数据');
      return;
    }
    if (!rawData?.trim()) {
      message.warning('请先输入原始数据');
      return;
    }

    try {
      const res = await luaDebug({
        input: rawData,
        luaScript: inputData,
      });

      if (res.success && res.result) {
        // 分别设置解析后数据和日志
        if (res.result.output !== undefined) {
          setParsedData(res.result.output);
        }
        if (res.result.logs !== undefined) {
          setLogsData(res.result.logs);
        }
        message.success('测试成功');
      } else {
        message.error(res.errorMsg || '测试失败');
      }
    } catch (error) {
      message.error('测试失败');
    }
  };

  // Monaco编辑器配置
  const editorOptions = {
    selectOnLineNumbers: true,
    roundedSelection: false,
    readOnly: false,
    cursorStyle: 'line' as const,
    automaticLayout: true,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    lineNumbers: 'on' as const,
  };

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '6px' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 500, margin: 0 }}>Lua脚本编辑器</h2>
        <p style={{ fontSize: '14px', color: '#4b5563', marginTop: '8px', marginBottom: '16px', marginLeft: 0, marginRight: 0 }}>
          用户可以编写Lua脚本解析脚本对原始数据进行解析。
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: 500 }}>开启</span>
          <Switch checked={enabled} onChange={setEnabled} />
        </div>
      </div>

      {enabled && (
        <>
          <div style={{ padding: '16px 16px 0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 500 }}>原始数据</span>
              <Button type="text" icon={<DeleteOutlined />} onClick={handleClear}>
                清空
              </Button>
            </div>
            <div style={{ border: '1px solid #d1d5db', borderRadius: '4px', height: '100px' }}>
              <MonacoEditor
                language="plaintext"
                theme="vs-dark"
                value={rawData}
                options={editorOptions}
                onChange={(value) => setRawData(value)}
                editorDidMount={(editor) => {
                  editor.updateOptions({ wordWrap: 'on' });
                }}
              />
            </div>
          </div>
          <div style={{ padding: '0 16px' }}>
            <div style={{ marginTop: '8px', display: 'flex', gap: '16px', height: '400px', overflow: 'hidden' }}>
              {/* 输入数据 */}
              <div style={{ flex: '1', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>Lua解析脚本</div>
                <div style={{ border: '1px solid #d1d5db', borderRadius: '4px', height: '360px', overflow: 'hidden' }}>
                  <MonacoEditor
                    language="lua"
                    theme="vs-dark"
                    value={inputData}
                    options={editorOptions}
                    onChange={(value) => setInputData(value)}
                    editorDidMount={(editor) => {
                      inputEditorRef.current = editor;
                    }}
                  />
                </div>
              </div>

              {/* 解析后数据 */}
              <div style={{ flex: '1', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>解析后数据</div>
                <div style={{ border: '1px solid #d1d5db', borderRadius: '4px', height: '360px', overflow: 'hidden' }}>
                  <MonacoEditor
                    language="json"
                    theme="vs-dark"
                    value={parsedData}
                    options={{ ...editorOptions, readOnly: true }}
                    editorDidMount={(editor) => {
                      luaEditorRef.current = editor;
                    }}
                  />
                </div>
              </div>

              {/* 日志 */}
              <div style={{ flex: '1', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>日志</div>
                <div style={{ border: '1px solid #d1d5db', borderRadius: '4px', height: '360px', overflow: 'hidden' }}>
                  <MonacoEditor
                    language="plaintext"
                    theme="vs-dark"
                    value={logsData}
                    options={{ ...editorOptions, readOnly: true }}
                    editorDidMount={(editor) => {
                      outputEditorRef.current = editor;
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 底部操作按钮 */}
      <div
        style={{
          padding: '24px 32px 0 0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 16,
          borderTop: '1px solid #eee',
        }}
      >
        {enabled && (
          <>
            <ConfigProvider theme={{ token: { colorPrimary: '#52C41A' } }}>
              <Button type="primary" onClick={handleFormat} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={codeIcon} width={18} alt="" />
                  格式化
                </div>
              </Button>
            </ConfigProvider>
            <ConfigProvider theme={{ token: { colorPrimary: '#52C41A' } }}>
              <Button type="primary" onClick={handleTest} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={codeIcon} width={18} alt="" />
                  调试
                </div>
              </Button>
            </ConfigProvider>
          </>
        )}
        <Button type="primary" onClick={handleConfirm} icon={<SaveOutlined />} loading={saving}>
          保存
        </Button>
      </div>
    </div>
  );
};

// 弹窗版本的Lua脚本编辑器
export const LuaScriptModal: React.FC<{
  visible: boolean;
  product_id: string;
  onCancel: () => void;
  onSubmit: (scriptContent: string, enabled: boolean) => void;
  initialValue?: string;
  initialEnabled?: boolean;
  initialRawData?: string;
}> = ({
  visible,
  product_id,
  onCancel,
  onSubmit,
  initialValue = '',
  initialEnabled = false,
  initialRawData = '',
}) => {
  return (
    <Modal open={visible} footer={null} onCancel={onCancel} width={1440} destroyOnHidden centered>
      <LuaScriptEditor
        product_id={product_id}
        initialValue={initialValue}
        initialEnabled={initialEnabled}
        initialRawData={initialRawData}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default LuaScriptEditor;
